import React, { Fragment, useState, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    FormGroup,
    Label,
    Input,
    Alert
} from "reactstrap";
import { Btn, H5 } from "../../../../AbstractElements";
import DataTable from "react-data-table-component";
import axios from "axios";
import config from "../../../../config";
import { toast } from "react-toastify";


const fmt = (n) => (n != null ? Number(n).toLocaleString() : "—");

const METRICS = [
    { key: "gross_sales", label: "Gross Sales" },
    { key: "net_sales", label: "Net Sales" },
    { key: "tax", label: "Tax" },
    { key: "discount", label: "Discount" },
    { key: "returns", label: "Returns" },
    { key: "original_amount", label: "Original Amount" }
];

const ACTION_OPTIONS = [
    { value: "MANUAL_MATCH", label: "Manual Reconciliation" },
    { value: "REFETCH", label: "System Data Refetch" }
];

/* ─── MAIN COMPONENT ────────────────────────────────────────────────────────── */
const AuditReconciliation = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const token = localStorage.getItem("sessiontoken");
    const configheader = { headers: { Authorization: `${token}` } };
    const concessionaire_id = localStorage.getItem("concessionaireId");
    const login_name = localStorage.getItem("loginName");
    const login_id = localStorage.getItem("loginId");
    const TOLERANCE = Number(localStorage.getItem("auditTolerance") || 0);
    /* State passed from TenantAuditDetail */
    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);

    const auditPeriodId = location.state?.auditPeriodId;
    const monthLabel = location.state?.monthLabel;
    const selectedYear = location.state?.selectedYear;
    const fromPage = location.state?.from;
    const [action, setAction] = useState("MANUAL_MATCH");

    /* Editable uploaded values */
    const [editedValues, setEditedValues] = useState({});
    const [remarks, setRemarks] = useState("");
    const [validationMessage, setValidationMessage] = useState("");
    /* Fetch tenant if not passed via state */

    useEffect(() => {
        setLoading(true);
        axios.post(
            `${config.hostname}/audit/gettenantreconciledetails`,
            { tenant_audit_id: id },
            configheader
        )
            .then((res) => {
                setTenant(res.data);
            })
            .catch((err) => {
                console.error(err);
                toast.error(err?.response?.data?.message || "Failed to load reconciliation details");
            })
            .finally(() => setLoading(false));
    }, [id]);

    /* Seed editable values from uploaded data */
    useEffect(() => {
        if (!tenant) return;
        const upl = tenant?.audit_values?.UPLOADED || {};
        const initial = {};
        METRICS.forEach(({ key }) => {
            initial[key] = upl[key] != null ? String(upl[key]) : "";
        });
        setEditedValues(initial);
        setRemarks(tenant.discrepancy_remarks || "");
    }, [tenant]);

    const sys = tenant?.audit_values?.SYSTEM || {};
    const upl = tenant?.audit_values?.UPLOADED || {};

    /* ── Build table rows ──────────────────────────────────────────────────── */
    const tableData = useMemo(() => {
        return METRICS.map(({ key, label }) => {
            const sysVal = sys[key];
            const originalUpl = upl[key];
            const editedVal = editedValues[key];
            const editedNum = editedVal !== "" ? Number(editedVal) : null;
            const diff = sysVal != null && editedNum != null ? Number((editedNum - sysVal).toFixed(2)) : null;
            const withinTolerance = diff != null && Math.abs(diff) <= TOLERANCE;
            const isChanged = originalUpl != null && editedNum != null && editedNum !== originalUpl;

            return { key, label, sysVal, originalUpl, editedNum, diff, withinTolerance, isChanged };
        });
    }, [editedValues, sys, upl]);

    const handleValueChange = (key, value) => {
        setEditedValues((prev) => ({ ...prev, [key]: value }));
    };

    /* ── Columns ────────────────────────────────────────────────────────────── */
    const columns = [
        {
            name: "Metric",
            selector: (row) => row.label,
            grow: 1,
            cell: (row) => <span>{row.label}</span>
        },
        {
            name: "System Value",
            selector: (row) => row.sysVal,
            right: true,
            grow: 1,
            cell: (row) => (
                <span className="text-dark">{fmt(row.sysVal)}</span>
            )
        },
        {
            name: "Audited Value",
            selector: (row) => row.originalUpl,
            right: true,
            grow: 1,
            cell: (row) => (
                <span className={row.isChanged ? "text-warning f-w-600" : "text-muted"}>
                    {fmt(row.originalUpl)}
                </span>
            )
        },
        {
            name: "Reconciled Value",
            center: true,
            grow: 1,
            cell: (row) => (
                <Input
                    type="number"
                    bsSize="sm"
                    value={editedValues[row.key] ?? ""}
                    onChange={(e) => handleValueChange(row.key, e.target.value)}
                    style={{
                        width: "160px",
                        borderColor: row.isChanged ? "#f5a623" : "#ced4da",
                        background: row.isChanged ? "rgba(245,166,35,0.06)" : "#fff",
                        fontWeight: row.isChanged ? "600" : "400"
                    }}
                />
            )
        },
        {
            name: "Difference",
            right: true,
            grow: 1,
            cell: (row) => {

                if (row.diff == null) {
                    return <span className="text-muted">—</span>;
                }

                const color = row.withinTolerance ? "text-success" : "text-danger";

                return (
                    <span className={`${color} f-w-600`}>
                        {row.diff > 0 ? "+" : ""}
                        {Number(row.diff).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        })}
                    </span>
                );
            }
        }
    ];

    /* ── All metrics matched? ───────────────────────────────────────────────── */
    const allMatched = tableData.every(
        (row) => row.diff === 0
    );

    /* ── Save / Submit ──────────────────────────────────────────────────────── */

    const insertToLog = (task) => {
        const data = {
            created_by: login_id,
            concessionaire_id: concessionaire_id,
            module: "Audit",
            task: `${login_name} ${task}`
        }
        axios
            .post(`${config.hostname}/log`, data, configheader)
            .then(() => {
                console.log("inserted to log table")
            })
            .catch((error) => {
                toast.error('Error')
                console.log(error);

            });
    }
    
    const handleSave = () => {
        setValidationMessage("");

        if (!remarks || remarks.trim() === "") {
            setValidationMessage("Remarks are required to reconcile the audit.");
            return;
        }

        const hasValues = Object.values(editedValues).some(v => v !== "" && v !== null);

        if (!hasValues) {
            toast.error('Please enter reconciled values before saving');
            return;
        }
        const reconciledValues = {};

        METRICS.forEach(({ key }) => {
            reconciledValues[key] =
                editedValues[key] !== "" ? Number(editedValues[key]) : null;
        });

        axios
            .post(
                `${config.hostname}/audit/reconciletenantaudit`,
                {
                    tenant_audit_id: id,
                    audit_period_id: auditPeriodId,
                    reconciled_values: reconciledValues,
                    discrepancy_remarks: remarks,
                    action: action,
                    login_id: login_id,
                    tolerance: TOLERANCE,
                },
                configheader
            )
            .then((res) => {
                toast.success("Reconciliation saved successfully");
                const updatedTenant = res.data.tenantAudit;
                setTenant(updatedTenant);
                insertToLog(
                    `Performed reconciliation for ${tenant.tenant_name} (${tenant.property_name} - ${monthNames[monthLabel]} ${selectedYear})`
                );
                handleBack(updatedTenant);
            })
            .catch((err) => {
                toast.error("Failed to save reconciliation.");
            });
    };

    const handleBack = (updatedTenant = tenant) => {

        if (fromPage === "detail") {
            navigate(`${process.env.PUBLIC_URL}/admin/audit/tenant/${id}`, {
                replace: true,
                state: {
                    updatedTenant,
                    auditPeriodId,
                    monthLabel,
                    selectedYear,
                    property_id: location.state?.property_id,
                    propertyName: location.state?.propertyName
                }
            });

            return;
        }

        navigate(`${process.env.PUBLIC_URL}/admin/audit/default`, {
            state: {
                property_id: location.state?.property_id,
                property_name: location.state?.propertyName,
                month: monthLabel,
                year: selectedYear
            }
        });
    };
    /* ── Render ─────────────────────────────────────────────────────────────── */
    if (loading) return <div className="text-center p-5">Loading reconciliation data...</div>;
    if (!tenant) return <div className="text-center p-5">Tenant audit record not found.</div>;

    const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];

    const customStyles = {
        headCells: {
            style: {
                fontSize: "14px",
                fontWeight: "600",
                paddingLeft: "16px",
                paddingRight: "16px"
            }
        },
        cells: {
            style: {
                fontSize: "13px",
                paddingLeft: "16px",
                paddingRight: "16px"
            }
        },
        rows: {
            style: {
                minHeight: "52px"
            }
        }
    };

    return (
        <Fragment>

            {/* ── HEADER CARD ────────────────────────────────────────────────── */}
            <Card>
                <CardBody>
                    <Row className="align-items-center">
                        <Col md="8">
                            <H5 className="mb-0 f-w-600">
                                {tenant.tenant_name}
                            </H5>
                            <div className="text-muted mt-1" style={{ fontSize: "13px" }}>
                                {tenant.property_name} • {monthNames[monthLabel]} {selectedYear}
                            </div>
                        </Col>
                        <Col md="4" className="text-end">
                            <Btn attrBtn={{ color: "primary", onClick: handleBack }}>
                                Back
                            </Btn>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* ── RECONCILIATION TABLE ───────────────────────────────────────── */}
            <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                    <H5>Adjust Uploaded Values</H5>

                </CardHeader>

                <CardBody>
                    <DataTable
                        data={tableData}
                        columns={columns}
                        striped
                        highlightOnHover
                        pagination={false}
                        customStyles={customStyles}
                        noDataComponent="No data available"
                    />

                </CardBody>
            </Card>

            {/* ── REMARKS & ACTIONS ──────────────────────────────────────────── */}
            <Card>
                <CardHeader><H5>Reconciliation Details</H5></CardHeader>
                <CardBody>
                    <Row className="mb-3">
                        <Col md="4">
                            <FormGroup>
                                <Label>Activity</Label>
                                <Input
                                    type="select"
                                    value={action}
                                    onChange={(e) => setAction(e.target.value)}
                                >
                                    {ACTION_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label>Discrepancy Remarks</Label>
                                <Input
                                    type="textarea"
                                    rows={4}
                                    placeholder="Enter remarks about this reconciliation..."
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                />
                            </FormGroup>
                            {validationMessage && (
                                <span className="text-danger">{validationMessage}</span>
                            )}
                        </Col>
                    </Row>

                    <Row className="mt-2">
                        <Col md="12" className="text-end">
                            <Btn
                                attrBtn={{
                                    color: "primary",
                                    className: "me-3",
                                    onClick: handleBack
                                }}
                            >
                                Cancel
                            </Btn>
                            <Btn
                                attrBtn={{
                                    color: "primary",
                                    onClick: handleSave
                                }}
                            >
                                Save Reconciliation
                            </Btn>
                        </Col>
                    </Row>

                </CardBody>
            </Card>

        </Fragment>
    );
};

export default AuditReconciliation;